import { loginRequestApi } from '@utils/authConfig.ts'
import { msalInstance } from '@utils/msal.ts'
import { getCurrentTenant, type TenantId } from '@utils/tenant-utils'
import { environment } from '@/config/environment'
import { createLogger } from '@/utils'

// ---------------------------------------------------------------------------
// Post-process infrastructure
// ---------------------------------------------------------------------------

/** Resolved entry — internal use only. */
type TransformEntry = {
  readonly path: string[]
  readonly transform: (rawValue: unknown) => unknown
}

/** Unwraps array types: `SupplierModel[]` → `SupplierModel`, `T[]` → `T`. */
type ElementOf<T> = T extends ReadonlyArray<infer U> ? U : T

/**
 * A single transform entry: a proxy-based field selector paired with a
 * transform function that receives the raw wire value and returns the parsed result.
 *
 * The selector always navigates a **single element** of the response type.
 * When `T` is an array, the selector types `root` as the element — the apiClient
 * automatically applies the transform to every item in the array.
 */
export type TransformInput<T> = [
  select: (root: ElementOf<T>) => unknown,
  transform: (rawValue: unknown) => unknown,
]

/** Resolved transforms configuration — internal use only. */
type TransformsConfig = {
  readonly entries: ReadonlyArray<TransformEntry>
}

function extractPath<T>(selector: (root: T) => unknown): string[] {
  const path: string[] = []
  const makeProxy = (): T => {
    const proxy = new Proxy<Record<string, unknown>>(
      {},
      {
        get(_, prop: string) {
          path.push(prop)
          return makeProxy()
        },
      },
    )
    return proxy as unknown as T
  }
  selector(makeProxy())
  return path
}

function buildTransformsConfig<T>(entries: TransformInput<T>[]): TransformsConfig {
  return {
    entries: entries.map(([select, transform]) => ({
      path: extractPath<ElementOf<T>>(select),
      transform,
    })),
  }
}

function applyAtPath(
  obj: Record<string, unknown>,
  [head, ...tail]: string[],
  transform: (rawValue: unknown) => unknown,
): void {
  if (!(head in obj)) return
  if (tail.length === 0) {
    obj[head] = transform(obj[head])
    return
  }
  const child = obj[head]
  if (Array.isArray(child)) {
    child.forEach((item) => {
      if (item && typeof item === 'object')
        applyAtPath(item as Record<string, unknown>, tail, transform)
    })
  } else if (child && typeof child === 'object') {
    applyAtPath(child as Record<string, unknown>, tail, transform)
  }
}

function reviveWithPaths(data: unknown, config: TransformsConfig): unknown {
  if (Array.isArray(data)) {
    return data.map((item) => reviveWithPaths(item, config))
  }
  if (!data || typeof data !== 'object') return data

  const result = { ...(data as Record<string, unknown>) }
  for (const { path, transform } of config.entries) {
    applyAtPath(result, path, transform)
  }
  return result
}

// ---------------------------------------------------------------------------

export interface ApiRequestConfig<T = unknown> extends Omit<RequestInit, 'body'> {
  headers?: Record<string, string>
  params?: Record<string, string | number | boolean | undefined | (string | number | boolean)[]>
  timeout?: number
  body?: unknown
  responseType?: 'json' | 'text' | 'blob' | 'arrayBuffer'
  /**
   * Use ApiClient's in-flight request cache (dedupe). Default: false.
   * React Query should be the source of truth for caching in most cases.
   */
  useCache?: boolean
  // Retry configuration
  retries?: number
  retryDelayMs?: number
  retryOnStatuses?: number[]
  // Don't throw on specific status codes
  dontThrowOnStatuses?: number[]
  /**
   * Field transform entries applied after JSON parsing. Each entry is a
   * `[selector, transform]` pair — the selector navigates the response type
   * via a proxy (no string paths), and the transform receives the raw wire
   * value and returns the parsed result.
   *
   * @example
   * transforms: [
   *   [root => root.contactPerson?.contactPreference, v => ContactPreferenceOptions.from(v as string | null)],
   * ]
   */
  transforms?: TransformInput<T>[]
  /**
   * When set, the value is sent as the `If-Match` request header.
   * Use this to supply an ETag obtained from a previous GET or PUT response.
   */
  etag?: string | null
}

export interface ApiResponse<T> {
  data: T
  headers: Headers
  status: number
  statusText: string
  ok: boolean
}

export interface PagedResult<T> {
  items: T[]
  hasMoreResults: boolean
  continuationToken: string | null
}

/**
 * Transport-layer metadata carrying the ETag response header value.
 * Separated from domain types so it can be composed in or stripped via
 * TypeScript's structural assignability.
 */
export interface EtagMeta {
  readonly etag: string | null
}

/**
 * T extended with ETag transport metadata.
 * WithEtag<T> is structurally assignable to T — no unwrapping required.
 * The `etag` property is attached as a readonly enumerable property so it
 * survives TanStack Query structural sharing (which only copies enumerable
 * keys); it is visible to JSON.stringify, Object.keys, and spread.
 */
export type WithEtag<T extends object> = T & EtagMeta

export interface PagedRequestParams {
  tenantId?: TenantId
  pageSize: number
  continuationToken?: string | null
}

export class ApiError extends Error {
  constructor(
    public status: number,
    public statusText: string,
    public data?: unknown,
  ) {
    super(`API Error ${status}: ${statusText}`)
    this.name = 'ApiError'
  }
}

const logger = createLogger('API')
const isJsonContentType = (contentType: string): boolean =>
  contentType.includes('application/json') || contentType.includes('application/problem+json')

const tryParseJson = async <X>(
  response: Response,
  fallback: X,
  transforms?: TransformsConfig,
): Promise<X> => {
  try {
    const data = (await response.json()) as X
    return transforms ? (reviveWithPaths(data, transforms) as X) : data
  } catch {
    return fallback
  }
}

const parseResponseBody = async <T>(
  response: Response,
  responseType: ApiRequestConfig['responseType'],
  isJson: boolean,
  transforms?: TransformsConfig,
): Promise<T> => {
  if (response.status === 204) {
    return null as T
  }
  if (responseType === 'json') {
    return (isJson ? await tryParseJson<T>(response, null as T, transforms) : null) as T
  }
  if (responseType === 'text') {
    return (await response.text()) as unknown as T
  }
  if (responseType === 'blob') {
    return (await response.blob()) as unknown as T
  }
  return (await response.arrayBuffer()) as unknown as T
}

const buildRequestHeaders = (
  authHeaders: Record<string, string>,
  customHeaders: Record<string, string>,
  body: unknown,
): Record<string, string> => {
  const headers: Record<string, string> = { ...authHeaders, ...customHeaders }

  if (body && typeof body === 'object' && !(body instanceof FormData)) {
    headers['Content-Type'] = headers['Content-Type'] ?? 'application/json'
  }
  return headers
}

const computeBackoffDelay = (
  attempt: number,
  retryDelayMs: number,
  retryAfterHeader?: string | null,
): number => {
  const retryAfterMs = retryAfterHeader ? Number(retryAfterHeader) * 1000 : undefined
  return retryAfterMs ?? Math.min(2000, retryDelayMs * 2 ** (attempt - 1))
}

const buildApiResponse = <T>(response: Response, data: T): ApiResponse<T> => ({
  data,
  headers: response.headers,
  status: response.status,
  statusText: response.statusText,
  ok: response.ok,
})

export const ApiClient = {
  inFlightRequests: new Map<string, Promise<ApiResponse<unknown>>>(),
  get DEFAULT_TIMEOUT() {
    return environment.apiDefaultTimeoutMs
  },

  async handleAuthorizationError<T>(
    error: ApiError,
    originalRequest: () => Promise<ApiResponse<T>>,
  ): Promise<ApiResponse<T>> {
    if (error.status === 401 || error.status === 403) {
      const { getUserPermissions } = await import('@utils/userPermissions')
      const { queryClient } = await import('@/main')
      const { queryKeys } = await import('@services/queryOptions')

      const freshPermissions = await queryClient.fetchQuery({
        queryKey: queryKeys.userPermissions(),
        queryFn: () => getUserPermissions({ forceRefresh: true }),
      })

      if (!freshPermissions.hasSkogHubUserAccess) {
        const { router } = await import('@/main')
        await router.navigate({ to: '/unauthorized' })
        throw error
      }

      if (error.status === 401) {
        return await originalRequest()
      }
    }
    throw error
  },

  async getAuthHeaders() {
    const accounts = msalInstance.getAllAccounts()
    if (accounts.length === 0) {
      throw new Error('Ingen bruker er logget inn.')
    }

    const account = accounts[0]
    const tokenResponse = await msalInstance.acquireTokenSilent({
      account,
      ...loginRequestApi,
    })

    const headers: Record<string, string> = {
      // Standard OAuth Authorization header
      Authorization: `Bearer ${tokenResponse.accessToken}`,
      Accept: 'application/json',
    }

    try {
      const tenantId = getCurrentTenant()
      if (typeof tenantId === 'string' && tenantId.length > 0) {
        headers['x-tenant-id'] = tenantId
      } else {
        logger.warn('Invalid tenant ID from getCurrentTenant()', { tenantId })
        headers['x-tenant-id'] = 'VikenSkog'
      }
    } catch (error) {
      logger.error('Error getting tenant for API headers', { error })
      headers['x-tenant-id'] = 'VikenSkog'
    }

    return headers
  },

  buildUrl(
    path: string,
    params?: Record<string, string | number | boolean | undefined | (string | number | boolean)[]>,
  ) {
    // Build URL - use relative URLs in development for Vite proxy, absolute for production
    const apiBaseUrl = environment.apiBaseUrl
    let baseUrl: string
    if (path.startsWith('http')) {
      baseUrl = path
    } else if (apiBaseUrl.startsWith('http')) {
      // Production: use absolute APIM URL
      baseUrl = `${apiBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`
    } else {
      // Development: use relative path for Vite proxy
      baseUrl = `${apiBaseUrl}${path.startsWith('/') ? '' : '/'}${path}`
    }

    // For relative URLs (development), return relative URL string to let fetch handle it
    // For absolute URLs (production), create full URL object
    if (!baseUrl.startsWith('http')) {
      // Development: use relative URL string for Vite proxy
      let relativeUrl = baseUrl
      if (params) {
        const searchParams = new URLSearchParams()
        Object.entries(params).forEach(([key, value]) => {
          if (value == null) return
          if (typeof value === 'string' && value === '') return
          if (Array.isArray(value)) {
            value.forEach((item) => {
              if (typeof item === 'string' && item === '') return
              searchParams.append(key, String(item))
            })
          } else {
            searchParams.append(key, String(value))
          }
        })
        const queryString = searchParams.toString()
        if (queryString) {
          relativeUrl += `?${queryString}`
        }
      }
      return relativeUrl
    }

    // Production: create full URL object
    const url = new URL(baseUrl)

    if (params) {
      Object.entries(params).forEach(([key, value]) => {
        if (value == null) return
        if (typeof value === 'string' && value === '') return
        if (Array.isArray(value)) {
          value.forEach((item) => {
            if (typeof item === 'string' && item === '') return
            url.searchParams.append(key, String(item))
          })
        } else {
          url.searchParams.append(key, String(value))
        }
      })
    }

    return url.toString()
  },

  async executeRequest<T>(
    method: string,
    path: string,
    config: ApiRequestConfig<T> = {},
  ): Promise<ApiResponse<T>> {
    const authHeaders = await this.getAuthHeaders()
    const startTime = Date.now()

    const {
      headers: customHeaders = {},
      params,
      body,
      transforms: rawTransforms,
      timeout = this.DEFAULT_TIMEOUT,
      responseType = 'json',
      // Disable internal retries by default; rely on React Query to retry
      retries = 0,
      retryDelayMs = 300,
      retryOnStatuses = [429, 503, 502],
      ...restConfig
    } = config

    const transforms = rawTransforms ? buildTransformsConfig(rawTransforms) : undefined

    const url = this.buildUrl(path, params)
    const headers = buildRequestHeaders(authHeaders, customHeaders, body)

    const externalSignal = restConfig.signal
    let attempt = 0
    for (;;) {
      // Create a fresh AbortController per attempt to avoid carrying over an aborted signal
      const controller = new AbortController()

      // Wire external abort to the current attempt's controller
      const onExternalAbort = () => {
        controller.abort()
      }
      if (externalSignal) {
        if (externalSignal.aborted) {
          controller.abort()
        } else {
          externalSignal.addEventListener('abort', onExternalAbort, { once: true })
        }
      }

      const timeoutId = setTimeout(() => {
        controller.abort()
      }, timeout)
      try {
        const response = await fetch(url, {
          method,
          headers,
          body:
            body && typeof body === 'object' && !(body instanceof FormData)
              ? JSON.stringify(body)
              : (body as BodyInit | null | undefined),
          signal: controller.signal,
          ...restConfig,
        })

        clearTimeout(timeoutId)
        externalSignal?.removeEventListener('abort', onExternalAbort)

        const contentType = response.headers.get('content-type') ?? ''
        const isJson = isJsonContentType(contentType)

        // Parse the body BEFORE checking ok to preserve existing behavior
        const responseData = await parseResponseBody<T>(response, responseType, isJson, transforms)

        const duration = Date.now() - startTime

        if (!response.ok) {
          // Extract structured error if possible
          // Use already-parsed responseData as errorBody since response body stream is consumed
          const retryAfterHeader = response.headers.get('retry-after')
          const errorBody: unknown = responseData as unknown

          const shouldRetry = attempt < retries && retryOnStatuses.includes(response.status)
          if (shouldRetry) {
            attempt += 1
            const backoff = computeBackoffDelay(attempt, retryDelayMs, retryAfterHeader)
            logger.warn('API retry', {
              method,
              path,
              status: response.status,
              attempt,
              delayMs: backoff,
            })
            await new Promise((r) => setTimeout(r, backoff))
            continue
          }

          // Check if we should throw on this status code
          if (config.dontThrowOnStatuses?.includes(response.status)) {
            // Return the response with error data instead of throwing
            const apiResponse: ApiResponse<T> = buildApiResponse<T>(response, errorBody as T)
            ;(apiResponse as { ok: boolean }).ok = false
            return apiResponse
          }

          logger.warn('API http error', {
            method,
            path,
            status: response.status,
            durationMs: duration,
          })
          const error = new ApiError(response.status, response.statusText, errorBody)

          if (response.status === 401 || response.status === 403) {
            return await this.handleAuthorizationError(error, () =>
              this.executeRequest<T>(method, path, config),
            )
          }

          throw error
        }

        const apiResponse: ApiResponse<T> = buildApiResponse<T>(response, responseData)

        if (environment.isDevelopment) {
          logger.info('API', { method, path, status: response.status, durationMs: duration })
        }

        return apiResponse
      } catch (error) {
        clearTimeout(timeoutId)
        const duration = Date.now() - startTime

        // Re-throw structured API errors as-is
        if (error instanceof ApiError) {
          throw error
        }

        // Timeout
        const errorName = (error as { name?: string } | undefined)?.name
        if (errorName === 'AbortError') {
          if (attempt < retries) {
            attempt += 1
            const backoff = Math.min(2000, retryDelayMs * 2 ** (attempt - 1))
            logger.warn('API timeout retry', { method, path, attempt, delayMs: backoff })
            await new Promise((r) => setTimeout(r, backoff))
            continue
          }
          logger.warn('API timeout', { method, path, durationMs: duration })
          throw new ApiError(408, 'Request Timeout')
        }

        // Network error
        if (attempt < retries) {
          attempt += 1
          const backoff = Math.min(2000, retryDelayMs * 2 ** (attempt - 1))
          logger.warn('API network retry', { method, path, attempt, delayMs: backoff, error })
          await new Promise((r) => setTimeout(r, backoff))
          continue
        }

        logger.error('API network error', { method, path, durationMs: duration, error })
        throw new ApiError(0, 'Network Error', error)
      }
    }
  },

  async request<T>(method: string, path: string, config: ApiRequestConfig<T> = {}) {
    const urlForKey = this.buildUrl(path, config.params)
    const varyHeaders: string[] = [] // Add header names here if response varies by them
    const varyKey = varyHeaders.map((h) => config.headers?.[h] ?? '').join('|')
    const cacheKey = `${method}:${urlForKey}:${varyKey}`

    // Always de-duplicate in-flight GET requests
    const shouldUseCache = method === 'GET'

    if (shouldUseCache && this.inFlightRequests.has(cacheKey)) {
      const cachedResponse = this.inFlightRequests.get(cacheKey)
      if (cachedResponse) {
        return cachedResponse as Promise<ApiResponse<T>>
      }
    }

    const requestPromise = this.executeRequest<T>(method, path, config)
    // Attach a catch handler to prevent unhandled rejection warnings in environments
    // where consumers attach their handlers on a later microtask.
    void requestPromise.catch(() => undefined)

    if (shouldUseCache) {
      this.inFlightRequests.set(cacheKey, requestPromise)
      const clearInFlight = () => {
        this.inFlightRequests.delete(cacheKey)
      }

      void requestPromise.then(clearInFlight, clearInFlight)
    }

    return requestPromise
  },

  async get<T>(path: string, config?: ApiRequestConfig<T>) {
    return this.request<T>('GET', path, config)
  },

  async post<T>(path: string, data?: unknown, config?: ApiRequestConfig<T>) {
    return this.request<T>('POST', path, { ...config, body: data })
  },

  async put<T>(path: string, data?: unknown, config?: ApiRequestConfig<T>) {
    return this.request<T>('PUT', path, { ...config, body: data })
  },

  async patch<T>(path: string, data?: unknown, config?: ApiRequestConfig<T>) {
    return this.request<T>('PATCH', path, { ...config, body: data })
  },

  async delete<T>(path: string, config?: ApiRequestConfig<T>) {
    return this.request<T>('DELETE', path, config)
  },
}

/**
 * Convenience function for simple GET requests with proper error handling.
 *
 * Return type is conditional on T:
 * - `T extends unknown[]`  -> returns `Promise<T>`           (array responses carry no etag)
 * - otherwise              -> returns `Promise<WithEtag<T>>` (etag attached as readonly property)
 *
 * The `etag` property is enumerable so it survives TanStack Query structural
 * sharing, which only copies enumerable keys when merging refetched data into
 * the cache. It therefore shows up in JSON.stringify, Object.keys, and spread.
 *
 * @param path - The path to the API endpoint
 * @param paramsOrConfig - Query params or full request config
 * @param timeout - The timeout for the request
 * @returns The response data, with ETag metadata attached for non-array responses
 */
export async function apiGet<T extends object>(
  path: string,
  paramsOrConfig?: Record<string, string | number | boolean | undefined> | ApiRequestConfig<T>,
  timeout?: number,
): Promise<T extends unknown[] ? T : WithEtag<T>> {
  const isConfig =
    typeof paramsOrConfig === 'object' &&
    !Array.isArray(paramsOrConfig) &&
    ('headers' in paramsOrConfig ||
      'useCache' in paramsOrConfig ||
      'body' in paramsOrConfig ||
      'transforms' in paramsOrConfig)

  const config: ApiRequestConfig<T> = isConfig
    ? (paramsOrConfig as ApiRequestConfig<T>)
    : {
        params: paramsOrConfig as Record<string, string | number | boolean | undefined> | undefined,
        timeout,
      }

  const response = await ApiClient.get<T>(path, config)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const data = response.data

  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    Object.defineProperty(data, 'etag', {
      value: response.headers.get('etag'),
      enumerable: true,
      writable: false,
      configurable: true,
    })
  }

  return data as T extends unknown[] ? T : WithEtag<T>
}

/**
 * Convenience function for GET requests returning null on 404
 * @param path - The path to the API endpoint
 * @param params - The parameters to send in the request
 * @param timeout - The timeout for the request
 * @returns The response data or null if the status is 404
 */
export async function apiGetOrNull<T>(
  path: string,
  paramsOrConfig?: Record<string, string | number | boolean | undefined> | ApiRequestConfig<T>,
  timeout?: number,
): Promise<T | null> {
  const baseConfig: ApiRequestConfig<T> =
    typeof paramsOrConfig === 'object' &&
    !Array.isArray(paramsOrConfig) &&
    ('headers' in paramsOrConfig ||
      'useCache' in paramsOrConfig ||
      'body' in paramsOrConfig ||
      'transforms' in paramsOrConfig)
      ? (paramsOrConfig as ApiRequestConfig<T>)
      : {
          params: paramsOrConfig as
            | Record<string, string | number | boolean | undefined>
            | undefined,
          timeout,
        }

  const response = await ApiClient.get<T>(path, {
    ...baseConfig,
    dontThrowOnStatuses: [404],
  })

  if (!response.ok) {
    if (response.status === 404) {
      return null
    }
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

/**
 * Convenience function for paginated GET requests, reads pagination state from response headers.
 * @param path - The path to the API endpoint
 * @param pagination - Pagination parameters (tenantId, pageSize, continuationToken)
 * @param config - Additional request config
 * @returns A PagedResult containing items and pagination metadata
 */
export async function apiGetWithPagedResult<T>(
  path: string,
  pagination: PagedRequestParams,
  paramsOrConfig?: Record<string, string | number | boolean | undefined> | ApiRequestConfig<T[]>,
  timeout?: number,
): Promise<PagedResult<T>> {
  const config: ApiRequestConfig<T[]> =
    typeof paramsOrConfig === 'object' &&
    !Array.isArray(paramsOrConfig) &&
    ('headers' in paramsOrConfig ||
      'useCache' in paramsOrConfig ||
      'body' in paramsOrConfig ||
      'transforms' in paramsOrConfig ||
      'params' in paramsOrConfig)
      ? (paramsOrConfig as ApiRequestConfig<T[]>)
      : {
          params: paramsOrConfig as
            | Record<string, string | number | boolean | undefined>
            | undefined,
          timeout,
        }

  const headers: Record<string, string> = {
    'x-tenant-id': pagination.tenantId ?? getCurrentTenant(),
    'x-page-size': pagination.pageSize.toString(),
  }

  if (pagination.continuationToken) {
    headers['x-continuation-token'] = pagination.continuationToken
  }

  const response = await ApiClient.get<T[]>(path, {
    ...config,
    headers: { ...headers, ...config?.headers },
  })

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const continuationToken = response.headers.get('x-continuation-token')
  const hasMoreResults = response.headers.get('x-has-more-results')?.trim().toLowerCase() === 'true'

  return {
    items: response.data ?? [],
    hasMoreResults: hasMoreResults ?? false,
    continuationToken,
  }
}

/**
 * Convenience function for POST requests with proper error handling
 * @param path - The path to the API endpoint
 * @param data - The data to send in the request body
 * @param timeout - The timeout for the request
 * @returns The response data
 */
export async function apiPost<T>(
  path: string,
  data?: unknown,
  config?: ApiRequestConfig<T>,
): Promise<T> {
  const response = await ApiClient.post<T>(path, data, config)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}

/**
 * Convenience function for PUT requests with proper error handling
 * Attaches the response ETag as a readonly enumerable `etag` property,
 * mirroring the behaviour of `apiGet`.
 * @param path - The path to the API endpoint
 * @param data - The data to send in the request body
 * @param config - Optional request configuration
 * @returns The response data with ETag attached (unless the response is an array)
 */
export async function apiPut<T extends object>(
  path: string,
  data?: unknown,
  config?: ApiRequestConfig<T>,
): Promise<T extends unknown[] ? T : WithEtag<T>> {
  const { etag, ...restConfig } = config ?? {}
  const configWithEtag: ApiRequestConfig<T> = {
    ...restConfig,
    headers: {
      ...restConfig.headers,
      ...(etag != null ? { 'If-Match': etag } : {}),
    },
  }
  const response = await ApiClient.put<T>(path, data, configWithEtag)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const responseData = response.data

  if (typeof responseData === 'object' && responseData !== null && !Array.isArray(responseData)) {
    Object.defineProperty(responseData, 'etag', {
      value: response.headers.get('etag'),
      enumerable: true,
      writable: false,
      configurable: true,
    })
  }

  return responseData as T extends unknown[] ? T : WithEtag<T>
}

/**
 * Convenience function for PATCH requests with proper error handling
 * @param path - The path to the API endpoint
 * @param data - The data to send in the request body
 * @param config - Optional request configuration
 * @returns The response data
 */
export async function apiPatch<T extends object>(
  path: string,
  data?: unknown,
  config?: ApiRequestConfig<T>,
): Promise<T extends unknown[] ? T : WithEtag<T>> {
  const { etag, ...restConfig } = config ?? {}
  const configWithEtag: ApiRequestConfig<T> = {
    ...restConfig,
    headers: {
      ...restConfig.headers,
      ...(etag != null ? { 'If-Match': etag } : {}),
    },
  }
  const response = await ApiClient.patch<T>(path, data, configWithEtag)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  const responseData = response.data

  if (typeof responseData === 'object' && responseData !== null && !Array.isArray(responseData)) {
    Object.defineProperty(responseData, 'etag', {
      value: response.headers.get('etag'),
      enumerable: true,
      writable: false,
      configurable: true,
    })
  }

  return responseData as T extends unknown[] ? T : WithEtag<T>
}

/**
 * Convenience function for DELETE requests with proper error handling
 * @param path - The path to the API endpoint
 * @param config - Optional request configuration
 * @returns The response data
 */
export async function apiDelete<T>(path: string, config?: ApiRequestConfig<T>): Promise<T> {
  const response = await ApiClient.delete<T>(path, config)

  if (!response.ok) {
    throw new ApiError(response.status, response.statusText, response.data)
  }

  return response.data
}
